import {
  flow,
  get,
  join,
  keys,
  map,
  orderBy,
  slice,
  split,
  toUpper,
  compose,
  filter
} from 'lodash/fp';

/**
 * [addRankToPlayers]
 * @param {Array} players
 * @param {String | Function} sortProp [Sort prop to use for ranking. Uses lodash/sortBy.]
 * @return {Array} Same list of players with rank added
 */
export function addRankToPlayers(sortProp = 'score') {
  return (players = []) => {
    // const a = performance.now();
    const sortedPlayers = orderBy(sortProp, 'desc')(players);
    let rank = 1;
    const sortHash = {};
    for (let i = 0; i < sortedPlayers.length; i += 1) {
      const player = sortedPlayers[i];
      const prevPlayer = sortedPlayers[i - 1];
      if (prevPlayer && prevPlayer[sortProp] > player[sortProp]) {
        rank = i + 1;
      }
      sortHash[`${player[sortProp]}`] = rank;
    }
    return players.map(p => ({
      ...p,
      rank: sortHash[p[sortProp]]
    }));
  };
}

export const makeInitials = flow(
  split(' '),
  slice(0, 2),
  map(x => toUpper(x.slice(0, 1))),
  join('')
);

export const makeFirstNameInitials = name => {
  const parts = name.split(' ');

  const intials = parts.map((p, i) => (i !== parts.length - 1 ? p.slice(0, 1) : p));
  return intials.join(' ');
};

export function isPlayerDisabled(criteria) {
  const {
    playerQuota,
    teamQuota,
    playersPerSideCap,
    playersPerGame,
    minGamesPerEntry,
    playersSelected,
    teamsSelected,
    playerPositions,
    // teamPositions,
    // salaryRemaining,
    selectionsBySide,
    selectionsByGame,
    shareQuota,
    positionFilter
  } = criteria;

  return player => {
    // if (player.salary > salaryRemaining) {
    //   return true;
    // }

    // exception for when the active filter is captain:
    // don't look at other positions, if captain position is already filled disable all players
    if (positionFilter === 'captain') {
      const captainPos = playerPositions.find(position => position.id === 'captain');

      if (captainPos && captainPos.filled) {
        return true;
      }
    }

    if (
      // exception for captain contest: always consider captain position as filled if not filtered by captain
      playerPositions.every(
        pos =>
          (positionFilter !== 'captain' && pos.id === 'captain') ||
          pos.filled ||
          !player.positions.find(p => p.key === pos.id)
      )
    ) {
      return true;
    }

    if (selectionsBySide[player.teamId] === playersPerSideCap) {
      return true;
    }

    const selected = shareQuota ? playersSelected + teamsSelected : playersSelected;

    if (selected === playerQuota) {
      return true;
    }

    if (
      selectionsByGame &&
      get('max', playersPerGame) > 0 &&
      get(player.gameId, selectionsByGame) >= get('max', playersPerGame)
    ) {
      return true;
    }

    if (minGamesPerEntry > 1 && selectionsByGame) {
      const gameIds = keys(selectionsByGame);

      if (
        playerQuota + teamQuota - playersSelected <= minGamesPerEntry - gameIds.length &&
        gameIds.includes(player.gameId)
      ) {
        return true;
      }
    }

    return false;
  };
}

export function makePositionStr(positionMap) {
  const mapAbbr = map(val => get('abbr', positionMap[val]));

  return (positions = []) => {
    if (positions.length === 0) {
      return undefined;
    }
    const [primaryKey, ...secondaryKeys] = compose(
      map('key'),
      filter(p => !p.is_flex && !p.is_captain)
    )(positions);
    const primaryStr = get('abbr', positionMap[primaryKey]);

    if (!primaryStr) return undefined; // can happen with captain positions

    const secondaryStr = join(' ', mapAbbr(secondaryKeys));
    return secondaryKeys.length > 0 ? `${primaryStr} (${secondaryStr})` : primaryStr;
  };
}
